import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import {
  ActivityEventsQuery,
  LoginEventsQuery,
  LoginMethodEnum,
  LoginOutcomeEnum,
  PlatformAccessEnum,
  useActivityEventsQuery,
  useLoginEventsQuery,
} from "@dashboard/graphql";
import useDebounce from "@dashboard/hooks/useDebounce";
import { platformMessages } from "@dashboard/platformAccess/messages";
import { getTimeZone } from "@dashboard/timeZone/timeZone";
import { Box, Button, Input, Select, Text } from "@saleor/macaw-ui-next";
import { ReactNode, useState } from "react";
import { MessageDescriptor, useIntl } from "react-intl";

import { describeTarget, humanizeAction } from "./actions";
import { accessLogMessages } from "./messages";
import { describeUserAgent } from "./userAgent";

type LoginEventRow = NonNullable<LoginEventsQuery["loginEvents"]>[number];
type ActivityEventRow = NonNullable<ActivityEventsQuery["activityEvents"]>[number];

const PAGE = 100;
const ALL = "all";
const OK = "ok";
const FAILED = "failed";

const outcomeMessages: Record<LoginOutcomeEnum, MessageDescriptor> = {
  [LoginOutcomeEnum.SUCCESS]: accessLogMessages.success,
  [LoginOutcomeEnum.INVALID_CREDENTIALS]: accessLogMessages.invalidCredentials,
  [LoginOutcomeEnum.THROTTLED]: accessLogMessages.throttled,
  [LoginOutcomeEnum.ACCOUNT_BLOCKED]: accessLogMessages.accountBlocked,
  [LoginOutcomeEnum.PLATFORM_DENIED]: accessLogMessages.platformDenied,
  [LoginOutcomeEnum.OUTSIDE_HOURS]: accessLogMessages.outsideHours,
  [LoginOutcomeEnum.IP_BLOCKED]: accessLogMessages.ipBlocked,
  [LoginOutcomeEnum.CODE_SENT]: accessLogMessages.codeSent,
  [LoginOutcomeEnum.WRONG_CODE]: accessLogMessages.wrongCode,
  [LoginOutcomeEnum.CODE_EXPIRED]: accessLogMessages.codeExpired,
  [LoginOutcomeEnum.NOT_STAFF]: accessLogMessages.notStaff,
};

// PASSWORD = the dashboard/ops sign-in form; EMAIL_CODE = the gate in front
// of it (backend saleor/dashboard_gate).
const methodLabel: Record<LoginMethodEnum, MessageDescriptor> = {
  [LoginMethodEnum.PASSWORD]: accessLogMessages.methodPassword,
  [LoginMethodEnum.EMAIL_CODE]: accessLogMessages.methodEmailCode,
};

const outcomeColor = (outcome: LoginOutcomeEnum) => {
  if (outcome === LoginOutcomeEnum.SUCCESS) {
    return "success1";
  }

  // A code being sent is a normal step, not a failure.
  return outcome === LoginOutcomeEnum.CODE_SENT ? "default1" : "critical1";
};

const platformLabel: Record<PlatformAccessEnum, MessageDescriptor> = {
  [PlatformAccessEnum.DASHBOARD]: platformMessages.dashboard,
  [PlatformAccessEnum.OPS]: platformMessages.ops,
  [PlatformAccessEnum.PORTAL]: platformMessages.portal,
};

const cell = { padding: "8px 12px 8px 0", verticalAlign: "top" } as const;

const Table = ({ columns, children }: { columns: MessageDescriptor[]; children: ReactNode }) => {
  const intl = useIntl();

  return (
    <Box style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            {columns.map(column => (
              <th key={column.id} style={cell}>
                <Text size={2} fontWeight="bold">
                  {intl.formatMessage(column)}
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Box>
  );
};

const rowStyle = { borderTop: "1px solid var(--mu-colors-border-default1, #e0e3e8)" } as const;

const useWhen = () => {
  const intl = useIntl();

  // The dashboard-wide time zone is applied by src/timeZone (default Lisbon).
  return (iso: string) =>
    new Date(iso).toLocaleString(intl.locale, { dateStyle: "medium", timeStyle: "medium" });
};

const SignIns = () => {
  const intl = useIntl();
  const formatWhen = useWhen();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const applySearch = useDebounce((value: string) => setSearch(value), 300);
  const [outcome, setOutcome] = useState(ALL);
  const [method, setMethod] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [limit, setLimit] = useState(PAGE);
  const { data, loading, refetch } = useLoginEventsQuery({
    variables: {
      search: search || undefined,
      outcome: outcome === ALL ? undefined : (outcome as LoginOutcomeEnum),
      method: method === ALL ? undefined : (method as LoginMethodEnum),
      platform: platform === ALL ? undefined : (platform as PlatformAccessEnum),
      limit,
    },
    fetchPolicy: "network-only",
  });
  const events: LoginEventRow[] = data?.loginEvents ?? [];
  const allOption = { value: ALL, label: intl.formatMessage(accessLogMessages.all) };

  return (
    <>
      <Box display="flex" gap={3} style={{ flexWrap: "wrap" }} alignItems="flex-end">
        <Box style={{ width: 280 }}>
          <Input
            size="small"
            label={intl.formatMessage(accessLogMessages.search)}
            value={searchInput}
            onChange={event => {
              setSearchInput(event.target.value);
              applySearch(event.target.value);
              setLimit(PAGE);
            }}
          />
        </Box>
        <Select
          size="small"
          label={intl.formatMessage(accessLogMessages.outcomeFilter)}
          options={[
            allOption,
            ...Object.values(LoginOutcomeEnum).map(value => ({
              value,
              label: intl.formatMessage(outcomeMessages[value]),
            })),
          ]}
          value={{
            value: outcome,
            label:
              outcome === ALL
                ? allOption.label
                : intl.formatMessage(outcomeMessages[outcome as LoginOutcomeEnum]),
          }}
          onChange={option => {
            setOutcome(option?.value ?? ALL);
            setLimit(PAGE);
          }}
          style={{ width: 260 }}
        />
        <Select
          size="small"
          label={intl.formatMessage(accessLogMessages.methodFilter)}
          options={[
            allOption,
            ...Object.values(LoginMethodEnum).map(value => ({
              value,
              label: intl.formatMessage(methodLabel[value]),
            })),
          ]}
          value={{
            value: method,
            label:
              method === ALL
                ? allOption.label
                : intl.formatMessage(methodLabel[method as LoginMethodEnum]),
          }}
          onChange={option => {
            setMethod(option?.value ?? ALL);
            setLimit(PAGE);
          }}
          style={{ width: 200 }}
        />
        <Select
          size="small"
          label={intl.formatMessage(accessLogMessages.platformFilter)}
          options={[
            allOption,
            ...Object.values(PlatformAccessEnum).map(value => ({
              value,
              label: intl.formatMessage(platformLabel[value]),
            })),
          ]}
          value={{
            value: platform,
            label:
              platform === ALL
                ? allOption.label
                : intl.formatMessage(platformLabel[platform as PlatformAccessEnum]),
          }}
          onChange={option => {
            setPlatform(option?.value ?? ALL);
            setLimit(PAGE);
          }}
          style={{ width: 200 }}
        />
        <Button variant="secondary" onClick={() => refetch()}>
          {intl.formatMessage(accessLogMessages.refresh)}
        </Button>
      </Box>
      <DashboardCard>
        <DashboardCard.Content>
          {events.length === 0 && !loading ? (
            <Text size={2} color="default2">
              {intl.formatMessage(accessLogMessages.empty)}
            </Text>
          ) : (
            <Table
              columns={[
                accessLogMessages.columnWhen,
                accessLogMessages.columnWho,
                accessLogMessages.columnResult,
                accessLogMessages.columnMethod,
                accessLogMessages.columnPlatform,
                accessLogMessages.columnFrom,
                accessLogMessages.columnDevice,
              ]}
            >
              {events.map(event => (
                <tr key={event.id} style={rowStyle}>
                  <td style={cell}>
                    <Text size={2}>{formatWhen(event.createdAt)}</Text>
                  </td>
                  <td style={cell}>
                    <Text size={2} fontWeight="bold">
                      {event.userName ?? intl.formatMessage(accessLogMessages.unknownAccount)}
                    </Text>
                    <br />
                    <Text size={1} color="default2">
                      {event.email}
                    </Text>
                  </td>
                  <td style={cell}>
                    <Text size={2} color={outcomeColor(event.outcome)}>
                      {intl.formatMessage(outcomeMessages[event.outcome])}
                    </Text>
                  </td>
                  <td style={cell}>
                    <Text size={2}>{intl.formatMessage(methodLabel[event.method])}</Text>
                  </td>
                  <td style={cell}>
                    <Text size={2}>
                      {event.platform ? intl.formatMessage(platformLabel[event.platform]) : "—"}
                    </Text>
                  </td>
                  <td style={cell}>
                    <Text size={2}>{event.ipAddress ?? "—"}</Text>
                    {event.country && (
                      <>
                        <br />
                        <Text size={1} color="default2">
                          {event.country}
                        </Text>
                      </>
                    )}
                  </td>
                  <td style={cell} title={event.userAgent ?? ""}>
                    <Text size={2}>{describeUserAgent(event.userAgent)}</Text>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          {events.length >= limit && (
            <Box marginTop={4}>
              <Button variant="secondary" onClick={() => setLimit(limit + PAGE)}>
                {intl.formatMessage(accessLogMessages.loadMore)}
              </Button>
            </Box>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </>
  );
};

const Activity = () => {
  const intl = useIntl();
  const formatWhen = useWhen();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const applySearch = useDebounce((value: string) => setSearch(value), 300);
  const [result, setResult] = useState(ALL);
  const [platform, setPlatform] = useState(ALL);
  const [limit, setLimit] = useState(PAGE);
  const { data, loading, refetch } = useActivityEventsQuery({
    variables: {
      search: search || undefined,
      succeeded: result === ALL ? undefined : result === OK,
      platform: platform === ALL ? undefined : (platform as PlatformAccessEnum),
      limit,
    },
    fetchPolicy: "network-only",
  });
  const events: ActivityEventRow[] = data?.activityEvents ?? [];
  const allOption = { value: ALL, label: intl.formatMessage(accessLogMessages.all) };
  const resultOptions = [
    { value: ALL, label: intl.formatMessage(accessLogMessages.resultFilterAll) },
    { value: OK, label: intl.formatMessage(accessLogMessages.succeeded) },
    { value: FAILED, label: intl.formatMessage(accessLogMessages.failed) },
  ];

  return (
    <>
      <Box display="flex" gap={3} style={{ flexWrap: "wrap" }} alignItems="flex-end">
        <Box style={{ width: 320 }}>
          <Input
            size="small"
            label={intl.formatMessage(accessLogMessages.searchActivity)}
            value={searchInput}
            onChange={event => {
              setSearchInput(event.target.value);
              applySearch(event.target.value);
              setLimit(PAGE);
            }}
          />
        </Box>
        <Select
          size="small"
          label={intl.formatMessage(accessLogMessages.outcomeFilter)}
          options={resultOptions}
          value={resultOptions.find(option => option.value === result) ?? resultOptions[0]}
          onChange={option => {
            setResult(option?.value ?? ALL);
            setLimit(PAGE);
          }}
          style={{ width: 200 }}
        />
        <Select
          size="small"
          label={intl.formatMessage(accessLogMessages.platformFilter)}
          options={[
            allOption,
            ...Object.values(PlatformAccessEnum).map(value => ({
              value,
              label: intl.formatMessage(platformLabel[value]),
            })),
          ]}
          value={{
            value: platform,
            label:
              platform === ALL
                ? allOption.label
                : intl.formatMessage(platformLabel[platform as PlatformAccessEnum]),
          }}
          onChange={option => {
            setPlatform(option?.value ?? ALL);
            setLimit(PAGE);
          }}
          style={{ width: 200 }}
        />
        <Button variant="secondary" onClick={() => refetch()}>
          {intl.formatMessage(accessLogMessages.refresh)}
        </Button>
      </Box>
      <DashboardCard>
        <DashboardCard.Content>
          {events.length === 0 && !loading ? (
            <Text size={2} color="default2">
              {intl.formatMessage(accessLogMessages.empty)}
            </Text>
          ) : (
            <Table
              columns={[
                accessLogMessages.columnWhen,
                accessLogMessages.columnWho,
                accessLogMessages.columnAction,
                accessLogMessages.columnItems,
                accessLogMessages.columnResult,
                accessLogMessages.columnPlatform,
                accessLogMessages.columnFrom,
              ]}
            >
              {events.map(event => (
                <tr key={event.id} style={rowStyle}>
                  <td style={cell}>
                    <Text size={2}>{formatWhen(event.createdAt)}</Text>
                  </td>
                  <td style={cell}>
                    <Text size={2} fontWeight="bold">
                      {event.userName ?? event.email}
                    </Text>
                    {event.userName && (
                      <>
                        <br />
                        <Text size={1} color="default2">
                          {event.email}
                        </Text>
                      </>
                    )}
                  </td>
                  <td style={cell}>
                    <Text size={2}>{humanizeAction(event.action)}</Text>
                    {event.detail && (
                      <>
                        <br />
                        <Text size={1} color="default2">
                          {event.detail}
                        </Text>
                      </>
                    )}
                  </td>
                  <td style={cell}>
                    <Text size={1} color="default2">
                      {(event.targets ?? []).map(describeTarget).join(", ") || "—"}
                    </Text>
                  </td>
                  <td style={cell}>
                    <Text size={2} color={event.succeeded ? "success1" : "critical1"}>
                      {intl.formatMessage(
                        event.succeeded ? accessLogMessages.succeeded : accessLogMessages.failed,
                      )}
                    </Text>
                    {!event.succeeded && event.errorCodes && (
                      <>
                        <br />
                        <Text size={1} color="default2">
                          {event.errorCodes}
                        </Text>
                      </>
                    )}
                  </td>
                  <td style={cell}>
                    <Text size={2}>
                      {event.platform ? intl.formatMessage(platformLabel[event.platform]) : "—"}
                    </Text>
                  </td>
                  <td style={cell}>
                    <Text size={2}>{event.ipAddress ?? "—"}</Text>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          {events.length >= limit && (
            <Box marginTop={4}>
              <Button variant="secondary" onClick={() => setLimit(limit + PAGE)}>
                {intl.formatMessage(accessLogMessages.loadMore)}
              </Button>
            </Box>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </>
  );
};

type Tab = "signIns" | "activity";

export const AccessLog = () => {
  const intl = useIntl();
  const [tab, setTab] = useState<Tab>("signIns");
  const tabs: Array<{ id: Tab; label: MessageDescriptor }> = [
    { id: "signIns", label: accessLogMessages.tabSignIns },
    { id: "activity", label: accessLogMessages.tabActivity },
  ];

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(accessLogMessages.sectionName)} withoutBorder />
      <Box display="flex" flexDirection="column" gap={4} padding={6}>
        <Box display="flex" gap={2}>
          {tabs.map(({ id, label }) => (
            <Button
              key={id}
              variant={tab === id ? "primary" : "secondary"}
              onClick={() => setTab(id)}
              data-test-id={`access-log-tab-${id}`}
            >
              {intl.formatMessage(label)}
            </Button>
          ))}
        </Box>
        <Text size={2} color="default2">
          {intl.formatMessage(
            tab === "signIns"
              ? accessLogMessages.description
              : accessLogMessages.activityDescription,
          )}{" "}
          {intl.formatMessage(accessLogMessages.timeZoneNote, { timeZone: getTimeZone() })}
        </Text>
        {tab === "signIns" ? <SignIns /> : <Activity />}
      </Box>
    </ListPageLayout>
  );
};
